'use client';

import { IProductModel } from '@masterofmalt/atom.componentlibrary/models/internal/product/IProductModel';
import { useTranslations } from 'next-intl';
import { useFormStatus } from 'react-dom';
import { z } from 'zod';

import { Button } from '@/vibes/mom/primitives/button';
import { toast } from '@/vibes/mom/primitives/toaster';

import { Link } from '../link';

import { handleAddToCart } from './_actions/add-to-cart';

const errorSchema = z.union([
  z.string(),
  z.preprocess(
    (s) => {
      if (typeof s !== 'string') {
        throw new Error('Invalid JSON string');
      }

      return JSON.parse(s);
    },
    z.object({
      message: z.string(),
    }),
  ),
]);

function AddToCartButtonInner({ inStock }: { inStock: boolean }) {
  const t = useTranslations('Atom.Product');
  const { pending } = useFormStatus();

  return (
    <Button
      className="max-h-10 w-full font-normal @xl:w-56"
      disabled={!inStock}
      loading={pending}
      shape="rounded"
      type="submit"
      variant="buyButton"
    >
      {inStock ? t('addToCart') : t('outOfStock')}
    </Button>
  );
}

const SmartAddToCartButton = ({
  product,
  analyticsEvent,
}: {
  product: IProductModel;
  analyticsEvent?: () => void;
}) => {
  const t = useTranslations('Atom.Faceted');

  if (!product.bigCommerce) {
    throw new Error('Product is missing BigCommerce data');
  }

  if (!product.bigCommerce.sku) {
    throw new Error('Product is missing SKU');
  }

  // Create a wrapper for the server action
  const formActionWrapper = async (formData: FormData) => {
    const result = await handleAddToCart(formData);

    if (result.status === 'success') {
      if (analyticsEvent) {
        analyticsEvent();
      }

      toast.success(
        t.rich('addToCartSuccess', {
          cartLink: (chunks) => (
            <Link href="/cart" variant="underline">
              {chunks}
            </Link>
          ),
        }),
      );
    } else {
      let errorMessage = t('addToCartError');
      const errorObject = errorSchema.safeParse(result.error);

      if (errorObject.success) {
        if (typeof errorObject.data === 'string') {
          errorMessage = errorObject.data;
        } else if (errorObject.data.message) {
          errorMessage = errorObject.data.message;
        }

        if (errorMessage.includes('Not enough stock:')) {
          // Strip out the product ID from BC error message
          toast.error(errorMessage.replace('Not enough stock: ', '').replace(/\(\w.+\)\s{1}/, ''));
        } else if (errorMessage === 'NEXT_REDIRECT') {
          toast.error('An error occurred while adding to cart. Please try again.');
        } else {
          toast.error(errorMessage);
        }
      } else {
        toast.error(errorMessage);
      }

      // eslint-disable-next-line no-console
      console.error('Error adding product to cart:', result.error);
    }
  };

  return (
    <form action={formActionWrapper}>
      <input name="product_sku" type="hidden" value={product.bigCommerce.sku} />
      <input name="quantity" type="hidden" value="1" />
      <AddToCartButtonInner inStock={product.bigCommerce.availabilityV2.status === 'Available'} />
    </form>
  );
};

export default SmartAddToCartButton;
